import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { glossaryInputSchema, type GlossaryInput, type LocaleView } from '@agency/contracts';
import { Badge, Button, Drawer, EmptyState, Notice, SelectField, Skeleton, TextAreaField, TextField, useToast, scrollRegion } from '@agency/ui';
import { ApiClientError, del, get, put } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { QueryError } from '../../lib/query-state.tsx';
import { CheckField, ConfirmDrawer, errText, issueMap, orUndef, serverErrors, type Errors } from './common.tsx';

type Term = { id: string; term: string; kind: string; doNotTranslate: boolean; translations: Record<string, string>; notes: string | null };
const KINDS: { value: GlossaryInput['kind']; label: string }[] = [{ value: 'brand', label: 'Brand name' }, { value: 'product', label: 'Product name' }, { value: 'technical', label: 'Technical term' }, { value: 'phrase', label: 'Phrase' }];
const kindLabel = (k: string) => KINDS.find((x) => x.value === k)?.label ?? k;

export function GlossaryTab() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const edit = can('content.edit');
  const [editing, setEditing] = useState<{ term: Term | null } | null>(null);
  const [removing, setRemoving] = useState<Term | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const q = useQuery<Term[], ApiClientError>({ queryKey: ['site-glossary'], queryFn: () => get('/admin/site/glossary') });
  const remove = useMutation({
    mutationFn: (t: Term) => del(`/admin/site/glossary/${t.id}`),
    onSuccess: async (_v, t) => { await qc.invalidateQueries({ queryKey: ['site-glossary'] }); await qc.invalidateQueries({ queryKey: ['glossary'] }); toast({ tone: 'success', message: `Deleted “${t.term}”.` }); setRemoving(null); },
    onError: (e) => setRemoveError(errText(e)),
  });
  return (
    <section className="panel" aria-labelledby="gl-h">
      <div className="panel__head">
        <div><h2 id="gl-h">Glossary</h2><p className="ag-muted">Terms editors must keep as written, or translate a specific way, when preparing another language.</p></div>
        {edit ? <Button variant="primary" icon="plus" onClick={() => setEditing({ term: null })}>Add term</Button> : null}
      </div>
      {q.isLoading ? <Skeleton width="100%" height={160} /> : q.isError ? <QueryError error={q.error} onRetry={() => void q.refetch()} /> : q.data!.length === 0 ? (
        <EmptyState title="No terms yet" icon="file" action={edit ? <Button variant="primary" icon="plus" onClick={() => setEditing({ term: null })}>Add term</Button> : undefined}>Add brand names and technical terms so translations stay consistent.</EmptyState>
      ) : (
        <div className="ag-table-wrap" {...scrollRegion('Glossary')}>
          <table className="ag-table">
            <caption className="ag-visually-hidden">Glossary terms</caption>
            <thead><tr><th scope="col">Term</th><th scope="col">Type</th><th scope="col">Translation</th><th scope="col">Notes</th>{edit ? <th scope="col"><span className="ag-visually-hidden">Actions</span></th> : null}</tr></thead>
            <tbody>
              {q.data!.map((t) => {
                const langs = Object.entries(t.translations);
                return (
                  <tr key={t.id}>
                    <td>{t.term}</td>
                    <td>{kindLabel(t.kind)}</td>
                    <td>{t.doNotTranslate ? <Badge tone="warning">Do not translate</Badge> : langs.length ? <span className="ag-cell-2">{langs.map(([c, v]) => <span key={c}><span className="ag-mono">{c}</span> {v}</span>)}</span> : <span className="ag-muted">No translations</span>}</td>
                    <td style={{ maxWidth: 280 }}>{t.notes ?? <span className="ag-muted">None</span>}</td>
                    {edit ? (
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <Button size="sm" onClick={() => setEditing({ term: t })} aria-label={`Edit ${t.term}`}>Edit</Button>{' '}
                        <Button size="sm" variant="ghost" onClick={() => { setRemoveError(null); setRemoving(t); }} aria-label={`Delete ${t.term}`}>Delete…</Button>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!edit ? <p className="ag-muted">You can view the glossary but not change it.</p> : null}
      <Drawer open={!!editing} onClose={() => setEditing(null)} title={editing?.term ? `Edit “${editing.term.term}”` : 'Add term'} footer={null}>
        {editing ? <TermForm key={editing.term?.id ?? 'new'} term={editing.term} onClose={() => setEditing(null)} /> : null}
      </Drawer>
      <ConfirmDrawer open={!!removing} title="Delete term?" confirmLabel="Delete term" pending={remove.isPending} error={removeError} onClose={() => setRemoving(null)} onConfirm={() => removing && remove.mutate(removing)}>
        <p>“{removing?.term}” will no longer be offered to editors translating pages. Pages that already use it are not changed.</p>
      </ConfirmDrawer>
    </section>
  );
}

function TermForm({ term, onClose }: { term: Term | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const locales = useQuery<LocaleView[], ApiClientError>({ queryKey: ['locale-options', 'settings'], queryFn: () => get('/admin/site/locale-options') });
  const targets = (locales.data ?? []).filter((l) => l.enabled && !l.isDefault);
  const [v, setV] = useState({ term: term?.term ?? '', kind: (term?.kind ?? 'technical') as GlossaryInput['kind'], doNotTranslate: term?.doNotTranslate ?? false, notes: term?.notes ?? '' });
  const [tr, setTr] = useState<Record<string, string>>(term?.translations ?? {});
  const [errors, setErrors] = useState<Errors>({});
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (body: GlossaryInput) => put<Term>('/admin/site/glossary', body),
    onSuccess: async (r) => { await qc.invalidateQueries({ queryKey: ['site-glossary'] }); await qc.invalidateQueries({ queryKey: ['glossary'] }); toast({ tone: 'success', message: `“${r.term}” saved.` }); onClose(); },
    onError: (e) => { setErrors(serverErrors(e)); setError(errText(e)); },
  });
  function submit() {
    setError(null);
    // Languages not shown here (disabled since) keep their stored translation; blanks are dropped. A do-not-translate term carries none.
    const translations = v.doNotTranslate ? {} : Object.fromEntries(Object.entries(tr).map(([c, t]) => [c, t.trim()]).filter(([, t]) => t));
    const p = glossaryInputSchema.safeParse({ term: v.term, kind: v.kind, doNotTranslate: v.doNotTranslate, translations, notes: orUndef(v.notes) });
    if (!p.success) { setErrors(issueMap(p.error.issues)); return; }
    setErrors({});
    save.mutate(p.data);
  }
  const hidden = Object.keys(tr).filter((c) => !targets.some((l) => l.code === c) && tr[c]);
  return (
    <form noValidate className="formgrid" onSubmit={(e) => { e.preventDefault(); submit(); }}>
      {error ? <Notice tone="error" title="Could not save the term">{error}</Notice> : null}
      <TextField data-autofocus label="Term" required readOnly={!!term} value={v.term} error={errors.term} hint={term ? 'The term cannot be changed. Delete it and add a new one to rename.' : undefined} onChange={(e) => setV({ ...v, term: e.target.value })} />
      <SelectField label="Type" value={v.kind} error={errors.kind} onChange={(e) => setV({ ...v, kind: e.target.value as GlossaryInput['kind'] })}>
        {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
      </SelectField>
      <CheckField label="Do not translate" checked={v.doNotTranslate} hint="Editors keep this term exactly as written in every language." onChange={(doNotTranslate) => setV({ ...v, doNotTranslate })} />
      {!v.doNotTranslate ? (
        <fieldset className="rep-set">
          <legend>Approved translations</legend>
          {locales.isLoading ? <Skeleton width="100%" height={40} /> : locales.isError ? <Notice tone="error" title="Could not load languages">{errText(locales.error)}</Notice> : targets.length === 0 ? <p className="ag-muted" style={{ margin: 0 }}>No enabled language other than the default. Enable one under Languages to add translations.</p> : targets.map((l) => (
            <TextField key={l.code} label={`${l.name} (${l.code})`} value={tr[l.code] ?? ''} error={errors[`translations.${l.code}`]} onChange={(e) => setTr({ ...tr, [l.code]: e.target.value })} />
          ))}
          {hidden.length ? <p className="ag-hint" style={{ margin: 0 }}>Also stored for {hidden.join(', ')} (languages not currently enabled). Kept as is.</p> : null}
        </fieldset>
      ) : term && Object.keys(term.translations).length ? <Notice tone="warning" title="Translations will be removed" live={false}>Saving as “do not translate” clears the translations stored for this term.</Notice> : null}
      <TextAreaField label="Notes" rows={2} value={v.notes} error={errors.notes} hint={`${v.notes.length}/240`} onChange={(e) => setV({ ...v, notes: e.target.value })} />
      <div className="actions__row">
        <Button type="submit" variant="primary" loading={save.isPending}>{term ? 'Save changes' : 'Add term'}</Button>
        <Button onClick={onClose}>Cancel</Button>
      </div>
    </form>
  );
}
